import { useEffect, useState } from "react";
import { cn } from "../utils/cn";

// ── Icons ─────────────────────────────────────────────────────────────────────

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  href: string;
  isActive?: boolean;
}

export interface NavbarProps {
  logo: React.ReactNode;
  navItems: NavItem[];
  rightSection?: React.ReactNode;
  isSticky?: boolean;
  transparentOnTop?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Navbar({
  logo,
  navItems,
  rightSection,
  isSticky = false,
  transparentOnTop = false,
}: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!transparentOnTop) return;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOnTop]);

  const isTransparent = transparentOnTop && !scrolled && !mobileOpen;

  return (
    <header
      className={cn(
        "z-[1030] w-full transition-all duration-300",
        isSticky ? "sticky top-0" : "relative",
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "bg-white/90 dark:bg-surface/90 backdrop-blur-sm border-b border-border-default",
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="shrink-0">{logo}</div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-0.5" aria-label="Main navigation">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              aria-current={item.isActive ? "page" : undefined}
              className={cn(
                "relative px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150",
                item.isActive
                  ? "text-brand-purple dark:text-violet-400"
                  : isTransparent
                    ? "text-white/90 hover:text-white"
                    : "text-content-secondary hover:text-content-primary",
              )}
            >
              {item.label}
              {/* Active / hover underline */}
              <span
                aria-hidden="true"
                className={cn(
                  "absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-brand-purple",
                  "transition-transform duration-200 origin-center",
                  item.isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100",
                )}
              />
            </a>
          ))}
        </nav>

        {/* Right section + hamburger */}
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-3">{rightSection}</div>
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className={cn(
              "md:hidden p-2 rounded-md transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-1",
              isTransparent
                ? "text-white/90 hover:text-white"
                : "text-content-secondary hover:text-content-primary hover:bg-surface-overlay dark:hover:bg-surface-overlay",
            )}
          >
            {mobileOpen ? <XIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile slide-down panel */}
      <div
        className={cn(
          "md:hidden overflow-hidden transition-all duration-300 ease-out",
          "bg-white dark:bg-surface",
          "border-t border-border-default",
          mobileOpen ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <nav className="px-4 py-3 flex flex-col gap-0.5" aria-label="Mobile navigation">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              aria-current={item.isActive ? "page" : undefined}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-150",
                item.isActive
                  ? "bg-violet-50 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300 border-l-2 border-violet-600 dark:border-violet-400"
                  : "text-content-secondary hover:text-content-primary hover:bg-surface-overlay dark:hover:bg-surface-overlay",
              )}
            >
              {item.label}
            </a>
          ))}
          {rightSection && (
            <div className="pt-3 mt-2 border-t border-border-default flex flex-col gap-2">
              {rightSection}
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

Navbar.displayName = "Navbar";
